import { useCallback, useMemo, useState } from 'react'
import { BaseSensorEntry } from 'types'

interface AdvancedFilters {
  searchMode: 'AND' | 'OR'
  useWildcards: boolean
  selectedUnits: string[]
  keywords: string[]
}

export const useDatapointSelector = (
  tags: BaseSensorEntry[],
  selectedTags: BaseSensorEntry[],
  setSelectedTags: React.Dispatch<React.SetStateAction<BaseSensorEntry[]>>,
  onToggleTags?: (tags: BaseSensorEntry[]) => void,
  onRemoveTags?: (tags: BaseSensorEntry[]) => void
) => {
  const [advancedFilters, setAdvancedFilters] = useState<AdvancedFilters>({
    searchMode: 'AND',
    useWildcards: false,
    selectedUnits: [],
    keywords: [],
  })

  const [searchQuery, setSearchQuery] = useState('')
  const [showAdvancedFilter, setShowAdvancedFilter] = useState(false)

  const quickSearchResults = useMemo(() => {
    if (!searchQuery) return tags

    const query = searchQuery.toLowerCase()
    return tags.filter((tag) => {
      const searchableText =
        `${tag.id} ${tag.data.name} ${tag.data.description || ''}`.toLowerCase()
      return searchableText.includes(query)
    })
  }, [tags, searchQuery])

  // Tag selector handlers
  const handleTagToggle = useCallback(
    (tag: BaseSensorEntry) => {
      const isSelected = selectedTags.some((t) => t.id === tag.id)

      if (isSelected) {
        const newSelection = selectedTags.filter((t) => t.id !== tag.id)
        setSelectedTags(newSelection)
        if (onToggleTags) {
          onToggleTags(newSelection)
        }
      } else {
        const newSelection: BaseSensorEntry[] = [...selectedTags, tag]
        setSelectedTags(newSelection)
        if (onToggleTags) {
          onToggleTags(newSelection)
        }
      }
    },

    [selectedTags, setSelectedTags, onToggleTags]
  )
  const handleAdvancedFilterApply = useCallback(
    (filters: AdvancedFilters, newSelectedTags: BaseSensorEntry[]) => {
      setAdvancedFilters(filters)
      setSelectedTags(newSelectedTags)
      setShowAdvancedFilter(false)
      if (onToggleTags) {
        onToggleTags(newSelectedTags)
      }
    },
    [setSelectedTags, onToggleTags]
  )

  const handleRemoveTag = useCallback(
    (tagId: string) => {
      const newSelection = selectedTags.filter((t) => t.id !== tagId)
      setSelectedTags(newSelection)
      if (onRemoveTags) {
        onRemoveTags(newSelection)
      }
    },
    [selectedTags, setSelectedTags, onRemoveTags]
  )

  return {
    tags,
    searchQuery,
    setSearchQuery,
    showAdvancedFilter,
    setShowAdvancedFilter,
    advancedFilters,
    setAdvancedFilters,
    selectedTags,
    setSelectedTags,
    quickSearchResults,
    handleTagToggle,
    handleRemoveTag,
    handleAdvancedFilterApply,
  }
}
